// Metric ↔ imperial mapping for calculator input fields and result units.
// The deterministic formulas in calculators.js are written in field-standard
// IMPERIAL units. When the user selects metric, we:
//   - Display each field with its metric label & placeholder
//   - Convert user-entered metric input → imperial BEFORE calling the formula
//   - Convert formula result → metric AFTER the formula returns
// Internal step-by-step text remains in imperial (the canonical computation).

// factor: 1 unit_imperial = factor unit_metric  (m = i * factor)
const MAP = {
  ppg:        { metricUnit: "kg/m³",       factor: 119.826,  decimals: 1 },
  psi:        { metricUnit: "bar",         factor: 0.0689476, decimals: 3 },
  "psi/ft":   { metricUnit: "kPa/m",       factor: 22.6206,   decimals: 3 },
  bar:        { metricUnit: "bar",         factor: 1,         decimals: 3 },
  bbl:        { metricUnit: "m³",          factor: 0.158987,  decimals: 3 },
  gal:        { metricUnit: "L",           factor: 3.78541,   decimals: 2 },
  ft:         { metricUnit: "m",           factor: 0.3048,    decimals: 2 },
  in:         { metricUnit: "mm",          factor: 25.4,      decimals: 1 },
  "in²":      { metricUnit: "cm²",         factor: 6.4516,    decimals: 3 },
  gpm:        { metricUnit: "L/min",       factor: 3.78541,   decimals: 1 },
  "bbl/min":  { metricUnit: "m³/min",      factor: 0.158987,  decimals: 3 },
  "bbl/stk":  { metricUnit: "m³/stk",      factor: 0.158987,  decimals: 5 },
  "bbl/ft":   { metricUnit: "L/m",         factor: 521.629,   decimals: 2 },
  "bbl/d":    { metricUnit: "m³/d",        factor: 0.158987,  decimals: 3 },
  "ft/min":   { metricUnit: "m/min",       factor: 0.3048,    decimals: 2 },
  "ft³/sx":   { metricUnit: "L/sx",        factor: 28.3168,   decimals: 2 },
  "gal/sx":   { metricUnit: "L/sx",        factor: 3.78541,   decimals: 2 },
  scf:        { metricUnit: "Sm³",         factor: 0.02832,   decimals: 2 },
  "scf/bbl":  { metricUnit: "Sm³/m³",      factor: 0.178107,  decimals: 3 },
  "lb/ft":    { metricUnit: "kg/m",        factor: 1.48816,   decimals: 2 },
  "lb/sx":    { metricUnit: "kg/sx",       factor: 0.453592,  decimals: 2 },
  "lb/100ft²":{ metricUnit: "Pa",          factor: 0.4788,    decimals: 2 },
  lbf:        { metricUnit: "N",           factor: 4.44822,   decimals: 1 },
  lb:         { metricUnit: "kg",          factor: 0.453592,  decimals: 2 },
};

// Units that are universal / non-convertible — keep label and value identical in metric mode.
const PASSTHROUGH = new Set([
  "", "—", "%", "°", "hr", "min", "sx", "stk", "strokes", "HP", "HP/in²", "BHP",
  "BPD", "BPD/psi", "cP", "deg/100 ft", "deg/100ft", "ft (ΔTVD)", "32nds (csv)", "(unitless)",
  "$", "$/bbl", "chemical units", "chemical/time", "vol/time", "psi/stroke (slope)",
  "% oil", "% solids", "% water",
]);

function lookup(unit) {
  if (!unit) return null;
  // Trim parenthetical suffixes for result units like "psi (BHP)" or "ppg (KMW)" or "psi (reduction)"
  const base = unit.replace(/\s*\(.*\)\s*$/, "").trim();
  return MAP[base] || MAP[unit] || null;
}

export function getDisplayUnit(imperialUnit, system) {
  if (system !== "metric") return imperialUnit;
  if (!imperialUnit || PASSTHROUGH.has(imperialUnit)) return imperialUnit;
  const m = lookup(imperialUnit);
  if (!m) return imperialUnit;
  // Preserve parenthetical suffix (e.g. "psi (BHP)" → "bar (BHP)")
  const suffix = imperialUnit.match(/\s*\(.*\)\s*$/)?.[0] || "";
  return `${m.metricUnit}${suffix}`;
}

export function toImperialValue(value, imperialUnit, system) {
  if (system !== "metric") return value;
  if (value === "" || value === null || value === undefined) return value;
  const n = Number(value);
  if (Number.isNaN(n)) return value;
  if (!imperialUnit || PASSTHROUGH.has(imperialUnit)) return n;
  const m = lookup(imperialUnit);
  if (!m) return n;
  return n / m.factor;
}

export function fromImperialValue(value, imperialUnit, system) {
  if (system !== "metric") return value;
  if (typeof value !== "number" || !isFinite(value)) return value;
  if (!imperialUnit || PASSTHROUGH.has(imperialUnit)) return value;
  const m = lookup(imperialUnit);
  if (!m) return value;
  const r = value * m.factor;
  const f = Math.pow(10, m.decimals);
  return Math.round(r * f) / f;
}

export function getSystemLabel(system) {
  return system === "metric" ? "METRIC" : "IMPERIAL";
}

// Live inline reading: given the value the user is currently typing (in the
// CURRENT display system) plus the field's imperial unit, return a short
// human-friendly string showing the same value in the OPPOSITE system.
// Returns null if the unit is not convertible or value is empty/invalid.
export function getOppositeReading(value, imperialUnit, currentSystem) {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  if (!isFinite(n)) return null;
  if (!imperialUnit || PASSTHROUGH.has(imperialUnit)) return null;
  const m = lookup(imperialUnit);
  if (!m) return null;
  if (currentSystem === "metric") {
    // value is metric → convert to imperial
    const imp = n / m.factor;
    return `≈ ${trim(imp, 4)} ${imperialUnit}`;
  }
  // imperial → metric
  const met = n * m.factor;
  const suffix = imperialUnit.match(/\s*\(.*\)\s*$/)?.[0] || "";
  return `≈ ${trim(met, m.decimals)} ${m.metricUnit}${suffix}`;
}

function trim(v, d) {
  if (!isFinite(v)) return String(v);
  const f = Math.pow(10, d);
  const r = Math.round(v * f) / f;
  return parseFloat(r.toFixed(d)).toString();
}

// =========================================================================
// CENTRAL CONVERSION HELPERS for input/extra MAPS.
// Calculator outputs `inputs` and `extras` are { key: value } objects where the
// key often embeds the imperial unit in parentheses, e.g. "MW (ppg)". When the
// display system is metric, convert the numeric value and rewrite the unit
// inside the key. Non-numeric values and non-convertible units are passed
// through unchanged.
// =========================================================================

function extractParenUnit(key) {
  const m = String(key || "").match(/\(([^)]+)\)\s*$/);
  return m ? m[1].trim() : null;
}

// Convert a single { key: value } pair according to the active system.
// Returns { key, value } with the same shape, possibly rewritten.
export function convertLabeledEntry(key, value, system) {
  if (system !== "metric") return { key, value };
  const unit = extractParenUnit(key);
  if (!unit) return { key, value };
  if (PASSTHROUGH.has(unit)) return { key, value };
  const m = lookup(unit);
  if (!m) return { key, value };
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return { key, value };
  const conv = n * m.factor;
  const f = Math.pow(10, m.decimals);
  const rounded = Math.round(conv * f) / f;
  const newKey = key.replace(/\(([^)]+)\)\s*$/, `(${m.metricUnit})`);
  return { key: newKey, value: rounded };
}

// Convert every { key: value } entry of an object map. Returns a new object,
// preserves entry order. Leaves untouched if system is not metric.
export function convertLabeledMap(obj, system) {
  if (!obj || system !== "metric") return obj;
  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    const c = convertLabeledEntry(k, v, system);
    out[c.key] = c.value;
  });
  return out;
}

// Convenience: produce both the converted display result and the converted
// display label for a calculator output `{ result, unit }` pair.
export function getDisplayResultAndUnit(result, imperialUnit, system) {
  return {
    displayResult: fromImperialValue(result, imperialUnit, system),
    displayUnit: getDisplayUnit(imperialUnit, system),
  };
}
